import { useState } from "react";
import { createGfLink, deleteGfLink } from "../../../../api/giftingClient";
import type { GfDealDetail, GfLink, GfLinkDestination } from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { ConfirmModal } from "../../modals/ConfirmModal";
import { GfButton, GfField, GfMono, GfTag, copyText } from "../../ui/GfUi";

const DEST_LABEL: Record<GfLinkDestination, string> = {
  shopify: "Shopify",
  rakuten: "楽天市場",
  amazon: "Amazon",
  other: "その他",
};
const DESTS = Object.keys(DEST_LABEL) as GfLinkDestination[];

/** 計測リンク（短縮URL）の発行・コピー・削除。楽天/Amazon は素のURLのみ（UTM を付けない） */
export function LinksSection({
  detail,
  onChanged,
  toast,
}: {
  detail: GfDealDetail;
  onChanged: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [destination, setDestination] = useState<GfLinkDestination>("shopify");
  const [destUrl, setDestUrl] = useState("");
  const [coupon, setCoupon] = useState("");
  const [label, setLabel] = useState("");
  const [removing, setRemoving] = useState<GfLink | null>(null);
  const [busy, setBusy] = useState(false);
  const bare = destination === "rakuten" || destination === "amazon";

  const run = async (fn: () => Promise<unknown>, okMsg: string): Promise<boolean> => {
    setBusy(true);
    try {
      await fn();
      toast(okMsg);
      await onChanged();
      return true;
    } catch (e) {
      toast(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const issue = async () => {
    if (!/^https?:\/\//.test(destUrl.trim())) return toast("遷移先URLは http(s):// から入力してください");
    const ok = await run(
      () =>
        createGfLink({
          deal_id: detail.id,
          influencer_id: detail.influencer_id,
          destination,
          dest_url: destUrl.trim(),
          coupon_code: coupon.trim() || null,
          label: label.trim() || null,
        }),
      "計測リンクを発行しました"
    );
    if (ok) {
      setDestUrl("");
      setCoupon("");
      setLabel("");
      setAdding(false);
    }
  };

  return (
    <section className="gf-panel-section">
      <div className="gf-panel-section-head">
        <div className="gf-panel-section-title">計測リンク</div>
        <button type="button" className="gf-link-btn" onClick={() => setAdding((v) => !v)}>
          {adding ? "閉じる" : "＋ リンクを発行"}
        </button>
      </div>
      {detail.links.length === 0 && !adding && <div className="gf-note">計測リンクはまだありません。</div>}
      {detail.links.map((l) => (
        <div className="gf-link-row" key={l.id}>
          <div className="gf-link-top">
            <GfTag>{DEST_LABEL[l.destination]}</GfTag>
            {l.label && <span className="gf-link-label">{l.label}</span>}
            {l.is_active === 0 && <GfTag tone="muted">停止中</GfTag>}
          </div>
          <GfMono className="gf-link-url">{l.short_url}</GfMono>
          <div className="gf-link-meta">
            クリック 累計 <GfMono>{l.clicks_total}</GfMono> ／ 直近7日 <GfMono>{l.clicks_7d}</GfMono>
            {l.coupon_code ? <> ／ クーポン <GfMono>{l.coupon_code}</GfMono></> : null}
          </div>
          <div className="gf-post-actions">
            <button
              type="button"
              className="gf-link-btn"
              onClick={() =>
                void copyText(l.short_url).then((ok) => toast(ok ? "リンクをコピーしました" : "コピーできませんでした"))
              }
            >
              コピー
            </button>
            <button type="button" className="gf-link-btn is-danger" disabled={busy} onClick={() => setRemoving(l)}>
              削除
            </button>
          </div>
        </div>
      ))}
      {adding && (
        <div className="gf-boxed gf-post-add">
          <GfField label="遷移先モール">
            <select className="gf-select" value={destination} onChange={(e) => setDestination(e.target.value as GfLinkDestination)}>
              {DESTS.map((d) => (
                <option key={d} value={d}>
                  {DEST_LABEL[d]}
                </option>
              ))}
            </select>
          </GfField>
          <GfField label="遷移先URL">
            <input className="gf-input is-mono" value={destUrl} onChange={(e) => setDestUrl(e.target.value)} placeholder="https://…" />
          </GfField>
          {bare && <div className="gf-note">楽天・Amazon は素のURLのまま転送します（UTM などのパラメータは付与しません）。</div>}
          <div className="gf-form-grid">
            <GfField label="クーポンコード（任意）">
              <input className="gf-input is-mono" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
            </GfField>
            <GfField label="ラベル（任意）">
              <input className="gf-input" value={label} onChange={(e) => setLabel(e.target.value)} />
            </GfField>
          </div>
          <div className="gf-actions">
            <GfButton size="sm" variant="primary" disabled={busy || !destUrl.trim()} onClick={() => void issue()}>
              発行する
            </GfButton>
            <GfButton size="sm" onClick={() => setAdding(false)}>
              やめる
            </GfButton>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!removing}
        title="計測リンクを削除します"
        message="削除すると、配布済みの短縮URLは使えなくなります。"
        confirmLabel="削除する"
        danger
        busy={busy}
        onConfirm={() => {
          const l = removing;
          if (!l) return;
          void run(() => deleteGfLink(l.id), "計測リンクを削除しました").then((ok) => ok && setRemoving(null));
        }}
        onClose={() => setRemoving(null)}
      />
    </section>
  );
}
